import { useCallback, useEffect, useRef, useState } from 'react';

const normalizeClusterId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

/**
 * Generic hook for a single linked Notepad cluster id (module-persisted).
 */
export function useEmbeddedNotepadClusterLink({
  enabled = true,
  fetchClusterLink,
  patchClusterLink,
  onLoadError,
}) {
  const [clusterId, setClusterId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const fetchStartedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchClusterLink();
      setClusterId(normalizeClusterId(data?.linked_notepad_cluster_id));
      setLoadedOnce(true);
    } catch (err) {
      onLoadError?.(err);
      setLoadedOnce(true);
    } finally {
      setLoading(false);
    }
  }, [fetchClusterLink, onLoadError]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    load();
  }, [enabled, load]);

  const saveClusterLink = useCallback(
    async (nextId) => {
      const prev = clusterId;
      const normalizedNext = normalizeClusterId(nextId);
      setClusterId(normalizedNext);
      setSaving(true);
      try {
        const data = await patchClusterLink(normalizedNext);
        const saved = normalizeClusterId(data?.linked_notepad_cluster_id);
        setClusterId(saved);
        return saved;
      } catch (err) {
        setClusterId(prev);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [clusterId, patchClusterLink],
  );

  const clearClusterLink = useCallback(
    () => saveClusterLink(null),
    [saveClusterLink],
  );

  return {
    clusterId,
    loading,
    saving,
    loadedOnce,
    saveClusterLink,
    clearClusterLink,
  };
}
